import * as React from 'react'
import Box from '@mui/material/Box'
import { useAppDispatch, useAppSelector } from '../../core/redux/hooks'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { CampaignListParams } from '../../campaigns/interfaces/campaigns'
import {
  FIRST_PAGE_CAMPAIGNS_TABLE,
  PER_PAGE_CAMPAIGNS_TABLE
} from '../../campaigns/constants'
import { PAGE, constants } from '../../commons/constants/constants'
import {
  setBackButtonRoute,
  setBackButtonTitle,
  setEntity
} from '../../ui/sidebar/slices'
import CampaignsList from '../../campaigns/CampaignsList'
import AgencyCampaignsListHeader from './AgenciesCampaignsListHeader'
import { getValueForForm } from '../../commons/utils/utils'
import { getAgencyByID } from '../slices'
import { CircularProgress } from '@mui/material'
import { RoutePath } from '../../core/routes/routePaths'
import { getCampaigns } from '../../campaigns/slices'

const AgencyCampaigns = (): React.ReactElement => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const { id } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const { campaigns, totalCampaigns, tenantID } = useAppSelector((state) => ({
    campaigns: state.campaigns.campaigns,
    totalCampaigns: state.campaigns.totalCampaigns,
    tenantID: state.auth.tenantID
  }))

  const handleFormChange = ({
    agencyID,
    tenantID,
    search,
    categories,
    page,
    perPage
  }: CampaignListParams): void => {
    Object.entries({
      agencyID,
      tenantID,
      search,
      categories,
      page,
      perPage
    }).forEach(([k, v]) => {
      const checkedValue = getValueForForm(k, v, searchParams.entries)
      setSearchParams((params) => {
        if (Array.isArray(checkedValue) && checkedValue.length === 0) {
          params.delete(k)
        } else if (checkedValue != null) {
          params.set(k, checkedValue as string)
          if (k !== PAGE) {
            params.set(PAGE, constants.MIN_PAGINATION_PER_PAGE.toString())
          }
        }
        return params
      })
    })
  }

  React.useEffect(() => {
    if (id != null) {
      void dispatch(getAgencyByID(id))
      dispatch(setEntity(constants.SIDEBAR_ENTITY_TYPES.AGENCY))
      dispatch(setBackButtonTitle(t`BackToAgencies`))
      dispatch(setBackButtonRoute(RoutePath.Agencies))
    }
    return () => {
      dispatch(setEntity(constants.SIDEBAR_ENTITY_TYPES.GENERAL))
    }
  }, [id, dispatch, t])

  React.useEffect(() => {
    if (id != null) {
      void dispatch(
        getCampaigns({
          categories: searchParams.get('categories')?.split(',') ?? [],
          tenantID,
          search: searchParams.get('search') ?? '',
          page: parseInt(
            searchParams.get('page') ?? FIRST_PAGE_CAMPAIGNS_TABLE.toString()
          ),
          perPage: parseInt(
            searchParams.get('perPage') ?? PER_PAGE_CAMPAIGNS_TABLE.toString()
          ),
          agencyID: id ?? searchParams.get('agencyID') ?? '',
          status: searchParams.get('status') ?? ''
        })
      )
    }
  }, [id, dispatch, searchParams, tenantID])

  return id != null && id !== ''
    ? (
      <Box className='flex-column'>
        <Box>
          <h1 className='title'>{t`Campaigns`}</h1>
        </Box>
        <AgencyCampaignsListHeader
          enableCategoryDropdown
          search={searchParams.get('search') ?? undefined}
          categories={searchParams.get('categories')?.split(',') ?? []}
          handleFormChange={handleFormChange}
        />
        <CampaignsList
          campaignsList={campaigns}
          total={totalCampaigns}
          handleFormChange={handleFormChange}
          page={parseInt(
            searchParams.get('page') ?? FIRST_PAGE_CAMPAIGNS_TABLE.toString()
          )}
          perPage={parseInt(
            searchParams.get('perPage') ?? PER_PAGE_CAMPAIGNS_TABLE.toString()
          )}
        />
      </Box>
      )
    : (
      <CircularProgress />
      )
}

export default AgencyCampaigns
